import { Box, Chip, TextField, Typography, Skeleton, IconButton, Tooltip, Select, MenuItem, FormControl, Divider, SelectChangeEvent, FormHelperText } from "@mui/material";
import React from "react";
import { AddButton } from "../button/button";
import CloseIcon from "@mui/icons-material/Close";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import { useParams } from "react-router-dom";
import { STATUS } from "../../utils/status";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/appStore";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface InviteEmailInterface {
    handleAddAllClick: () => void;
    inviteEmails: string;
    setInviteEmails: React.Dispatch<React.SetStateAction<string>>;
    handleClose: () => void;
    selectedStage?: string;
    setTaskLimit?: React.Dispatch<React.SetStateAction<string>>;
    taskLimit?: string;
    selectedOption?: string;
    setSelectedOption?: React.Dispatch<React.SetStateAction<string>>;
    selectedValue?: string;
    setSelectedValue?: React.Dispatch<React.SetStateAction<string>>;
    gigSource?: string;
    setGigSource?: React.Dispatch<React.SetStateAction<string>>;
    setSelectedCategory?: React.Dispatch<React.SetStateAction<string[]>>
    selectedCategory?: string[]
}

const InviteEmailsDrawer: React.FC<InviteEmailInterface> = ({
    handleAddAllClick,
    inviteEmails,
    setInviteEmails,
    handleClose,
    taskLimit,
    setTaskLimit,
    selectedOption,
    setSelectedOption,
    selectedValue,
    setSelectedValue,
    gigSource,
    setGigSource,
    setSelectedCategory,
    selectedCategory
}) => {
    const { id } = useParams();
    const getReviewStages = useSelector((state: RootState) => state.reviewStage.reviewer);
    const getReviewStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);
    const stageRoles = useSelector((state: RootState) => state.projects.roles.results);

    const handleInviteEmailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInviteEmails(event.target.value.trim());
    };

    const handleDeleteChip = (emailToDelete: string) => {
        const updatedEmails = inviteEmails
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email !== emailToDelete)
            .join(", ");
        setInviteEmails(updatedEmails);
    };

    const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (/^\d*$/.test(value) && setTaskLimit) {
            setTaskLimit(value);
        }
    };

    const handleOptionChange = (event: any) => {
        if (setSelectedOption) {
            setSelectedOption(event.target.value);
        }
    };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("Text");
    const emailsWithCommas = pastedText.replace(/\s+/g, ",");
    setInviteEmails(
      (prevEmails) => `${prevEmails}${prevEmails ? "," : ""}${emailsWithCommas}`
    );
  };

  const handleAddEmails = () => {
    const emails = inviteEmails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email !== "");
    setInviteEmails(emails.join(", "));
  };

    const handleClearAll = () => {
        setInviteEmails("");
    };

    const handleRoleChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        if (setSelectedCategory) setSelectedCategory(typeof value === 'string' ? value.split(',') : value);
    };

  const emailList = inviteEmails
    .split(",")
    .map((email) => email.trim())
    .filter((email) => email !== "");

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                    sx={{
                        fontFamily: "Montserrat",
                        fontWeight: "600",
                        fontSize: "18px",
                        color: "#10383D",
                        marginBottom: "8px",
                    }}
                >
                    {id ? 'Onboard Taskers' : 'Invite Experts'}
                </Typography>
                <IconButton onClick={handleClose} sx={{ padding: "4px" }}>
                    <CloseIcon sx={{ color: "#10383D" }} />
                </IconButton>
            </Box>
            <Divider />

            <Typography
                sx={{
                    fontFamily: "Montserrat",
                    fontWeight: "400",
                    fontSize: "12px",
                    color: "#10383D",
                    marginBottom: "10px",
                    marginTop: 4
                }}
            >
                Enter emails separated by commas that you want to {id ? 'onboard' : 'invite'}
            </Typography>
            <TextField
                id="invite-emails"
                variant="outlined"
                placeholder="Enter emails separated by commas"
                value={inviteEmails}
                onChange={handleInviteEmailsChange}
                fullWidth
                size="small"
                onBlur={handleAddEmails}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddEmails();
                    }
                }}
                onPaste={handlePaste}
            />
            <Box
                sx={{
                    position: "relative",
                    flexGrow: 1,
                    overflowY: "auto",
                    minHeight: "50px",
                    maxHeight: "calc(100vh - 160px)",
                    padding: "8px 0",
                    marginTop: "10px",
                    borderRadius: "4px",
                    border: emailList.length > 0 ? "1px solid #E0E0E0" : "none",
                }}
            >
                {emailList.length > 0 ? (
                    <>
                        <Tooltip title="Click this to clear all emails." arrow>
                            <IconButton
                                onClick={handleClearAll}
                                sx={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    zIndex: 10,
                                    backgroundColor: "#FFEB3B",
                                    borderRadius: "50%",
                                    padding: "8px",
                                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                    transition: "background-color 0.3s, transform 0.3s",
                                    "&:hover": {
                                        backgroundColor: "#FF9800",
                                        transform: "scale(1.1)",
                                    },
                                }}
                            >
                                <ClearAllIcon />
                            </IconButton>
                        </Tooltip>

                        {emailList.map((email, index) => (
                            <Chip
                                key={index}
                                label={email}
                                onDelete={() => handleDeleteChip(email)}
                                sx={{ backgroundColor: "#E3F2FD", color: "#10383D", margin: "4px" }}
                            />
                        ))}
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                flexGrow: 1,
                                minHeight: "20px",
                                padding: "12px",
                                marginTop: "12px",
                                borderRadius: "8px",
                                border: "1px solid #E0E0E0",
                                backgroundColor: "#F9FAFB",
                                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                height: '200px'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "16px",
                                    fontWeight: "500",
                                    fontFamily: "Montserrat",
                                    color: "#666",
                                    textAlign: "center",
                                    marginBottom: "12px",
                                }}
                            >
                                Entered emails will show up here.
                            </Typography>

                            <Box sx={{ display: "flex", gap: "8px" }}>
                                {[...Array(3)].map((_, index) => (
                                    <Skeleton
                                        key={index}
                                        variant="rectangular"
                                        width={120}
                                        height={32}
                                        sx={{ borderRadius: "16px" }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>

            {id &&
                <div style={{ display: "flex", flexDirection: "column", marginTop: 40 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            sx={{
                                fontFamily: "Montserrat",
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "24px",
                                letterSpacing: "0px",
                                textAlign: "left",
                                color: "#10383D",
                            }}
                        >
                            Onboarding Stage Name
                        </Typography>
                    </Box>

                    <FormControl fullWidth size="small" error={!getReviewStages?.results?.length}>
                        <Select
                            value={selectedOption}
                            onChange={handleOptionChange}
                            displayEmpty
                            disabled={getReviewStages?.results?.length === 0}
                        >
                            {getReviewStatus === STATUS.SUCESS &&
                                getReviewStages?.results?.map((option: any) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                        </Select>

                        {!getReviewStages?.results?.length && (
                            <FormHelperText>Create stages first before onboarding users.</FormHelperText>
                        )}
                    </FormControl>
                </div>
            }

            {id && <div style={{ display: 'flex', flexDirection: 'column', marginTop: 40 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>
                        Auto Allocation Task Limit
                    </Typography>
                    <Tooltip title="Maximum number of tasks that can be auto-allocated on a specific stage to an expert (Tasker/Reviewer), based on task availability.">
                        <InfoOutlinedIcon sx={{ marginLeft: '10px', fontSize: '13px', cursor: 'pointer' }} />
                    </Tooltip>
                </Box>

                <TextField
                    placeholder="Auto Allocation Task Limit"
                    name="task_limit"
                    type="number"
                    size="small"
                    fullWidth
                    value={taskLimit}
                    onChange={handleFieldChange}
                />
            </div>
            }

            {/* {id &&
                <div style={{ marginTop: 20 }}>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D' }}>Role based on the stage selected</Typography>
                    <Select
                        multiple
                        value={selectedCategory}
                        onChange={handleRoleChange}
                        displayEmpty
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                        renderValue={(selected) =>
                            selected.length === 0 ? 'Select Role' : stageRoles.results
                                .filter((role: { id: string; }) => selected.includes(role.id))
                                .map((role: { name: string; }) => role.name)
                                .join(', ')
                        }
                    >
                        <MenuItem value="" disabled>Select Role</MenuItem>
                        {stageRoles?.results.length > 0 && stageRoles?.results.map((role: { id: string; name: string; }) => (
                            <MenuItem key={role.id} value={role.id}>
                                {role.name}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            } */}

            <Box sx={{ marginTop: "auto", paddingTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                <AddButton
                    disabled={
                        id
                            ? emailList.length === 0 || taskLimit === ''
                            : emailList.length === 0
                    }
                    onClick={handleAddAllClick}
                    fullWidth
                >
                    {id ? 'Onboard All' : 'Invite All'}
                </AddButton>
            </Box>
        </Box>
    );
};

export default InviteEmailsDrawer;
